import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';

const InsightDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const insights = useAppSelector((state) => state.insights.items);
  const interviews = useAppSelector((state) => state.interviews.items);

  const insight = insights.find((item) => item.id === id);
  const relatedInterview = insight ? interviews.find((item) => item.id === insight.interviewId) : null;

  if (!insight) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="error">
          インサイトが見つかりませんでした
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          一覧に戻る
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" color="inherit">
          インサイト一覧
        </Link>
        <Typography color="text.primary">
          {insight.title}
        </Typography>
      </Breadcrumbs>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{ mb: 3 }}
      >
        一覧に戻る
      </Button>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {insight.title}
        </Typography>

        <Box sx={{ mb: 3 }}>
          {insight.tags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              sx={{
                mr: 1,
                mb: 1,
                backgroundColor: tag.color,
                color: 'white',
              }}
            />
          ))}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          作成日: {new Date(insight.createdAt).toLocaleDateString('ja-JP')} |
          更新日: {new Date(insight.updatedAt).toLocaleDateString('ja-JP')}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          概要
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
          {insight.summary}
        </Typography>

        <Typography variant="h6" gutterBottom>
          詳細内容
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 4,
            lineHeight: 1.8,
            whiteSpace: 'pre-line',
          }}
        >
          {insight.content}
        </Typography>

        {relatedInterview && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              関連インタビュー
            </Typography>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <QuestionAnswerIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    {relatedInterview.interviewee}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  実施日: {new Date(relatedInterview.date).toLocaleDateString('ja-JP')} |
                  インタビュアー: {relatedInterview.interviewer}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {relatedInterview.summary}
                </Typography>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to={`/interviews/${relatedInterview.id}`}
                  startIcon={<QuestionAnswerIcon />}
                >
                  インタビュー詳細を見る
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default InsightDetailPage;
